import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ChevronRight, Layers, LineChart, ShieldCheck, Wallet, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DerivTicker from '@/components/DerivTicker';
import CertificateShowcase from '@/components/CertificateShowcase';
import { productsAPI } from '@/lib/api';
import { formatInt } from '@/lib/format';

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/e45d83a5-74de-4d44-9a07-65988bc4857d/images/aa7df7ebbec375546942e7ee52007f9f50479ad315cc9e094bf5f5f3eb891839.png';

const STATS = [
  { label: 'Trader Rewards Paid', value: '$35,008+', mono: true },
  { label: 'Active Evaluators', value: '650+', mono: true },
  { label: 'Countries', value: '120+', mono: true },
  { label: 'Avg. Reward Speed', value: '< 3h', mono: true },
];

const FEATURES = [
  { icon: LineChart, title: 'Structured Evaluation Model', desc: 'Clear objectives across two phases designed to assess consistency and risk management.' },
  { icon: ShieldCheck, title: 'Transparent Trading Rules', desc: 'Equity-based drawdowns, no hidden conditions, no silent rule changes.' },
  { icon: Wallet, title: 'Performance-Based Rewards', desc: 'Earn up to 70% in performance-based rewards, settled in cryptocurrency.' },
  { icon: Layers, title: 'Scalable Account Progression', desc: 'Consistent traders unlock higher account allocations over time.' },
];

const STEPS = [
  { n: '01', t: 'Prove your edge',
    d: 'Begin your journey by trading in a simulated evaluation environment. Follow defined rules and demonstrate your ability with discipline and consistency.' },
  { n: '02', t: 'Pass the evaluation',
    d: 'Meet the required objectives across the evaluation phases — achieve targets, respect drawdown limits, and maintain consistent risk management.' },
  { n: '03', t: 'Access performance account',
    d: 'Upon successful completion, gain access to a simulated performance account — no targets, same structured rules, focus on execution.' },
  { n: '04', t: 'Earn rewards & scale',
    d: 'Earn up to 70% in performance-based rewards. Consistent traders unlock higher account allocations over time.' },
];

const FAQS = [
  { q: 'Is this a live trading account?', a: 'No. All trading is conducted on simulated accounts within a controlled evaluation environment.' },
  { q: 'What am I paying for?', a: 'The fee covers access to the evaluation program, including the trading environment, tools, and performance tracking system.' },
  { q: 'What happens after I pass?', a: 'You gain access to a simulated performance account where you can continue trading and become eligible for performance-based rewards.' },
  { q: 'Do you act as a broker or investment firm?', a: 'No. Traders Spring Group does not provide brokerage services, investment services, or manage client funds.' },
  { q: 'What is the One-Step Program?', a: 'Pay $20 today for access to a single-phase evaluation. Once you hit the 15% objective within our rules, you pay a per-size unlock fee to activate your funded simulated account. No admin approval is required before the unlock is paid.' },
];

// Product-type presentation for the "Choose your program" preview grid.
const PLAN_META = {
  one_step: { name: 'One-Step Program', tagline: 'Pay $20 · pass one phase · unlock funded.', tag: '1-PHASE EVALUATION',
              leverage: '1:100', daily_dd: 4.0, max_dd: 8.0, min_days: 3 },
  instant:  { name: 'Instant Funding', tagline: 'Skip the evaluation. Start on a performance account.', tag: 'DIRECT PERFORMANCE',
              leverage: '1:100', daily_dd: 3.0, max_dd: 6.0, min_days: 3 },
};

export default function Landing() {
  const [products, setProducts] = useState([]);
  useEffect(() => { productsAPI.list().then(setProducts).catch(() => {}); }, []);

  // Group active products by type into a preview list for the "Choose your program" grid.
  const productGrid = (() => {
    const g = {};
    for (const p of products) {
      if (!g[p.product_type]) g[p.product_type] = [];
      g[p.product_type].push(p);
    }
    return ['one_step', 'instant']
      .filter(t => (g[t] || []).length > 0)
      .map(t => {
        const sizes = g[t].sort((a, b) => a.account_size - b.account_size);
        return {
          type: t,
          sizes,
          from: Math.min(...sizes.map(p => p.price_usd || 0)),
          ...PLAN_META[t],
        };
      });
  })();

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-10 pb-20 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="chip chip-green mb-6"
            >
              <span className="pulse-dot" />
              <span>Live · Simulated accounts up to $200,000</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
            >
              Where Skilled Traders
              <br />
              <span className="text-[#00ff66]">Prove Their Edge.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg text-zinc-400 max-w-2xl"
            >
              Trade <span className="text-white">synthetic indices</span> in a structured evaluation environment.
              Demonstrate consistency, manage risk, and earn <span className="text-white">up to 70% in performance-based rewards</span>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <Link to="/plans" className="btn-primary" data-testid="hero-start-evaluation">
                Start Evaluation <ArrowRight size={16} />
              </Link>
              <Link to="/rules" className="btn-ghost" data-testid="hero-view-rules">
                View Program Rules
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-6 text-xs text-zinc-500 max-w-xl"
            >
              Built for disciplined traders focused on consistency, structure, and long-term growth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-8 border-t border-white/5"
            >
              {STATS.map(s => (
                <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                  <div className="label-xs mb-2">{s.label}</div>
                  <div className={`text-3xl font-mono tracking-tight ${s.mono ? '' : 'font-display'}`}>{s.value}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* floating mock dashboard card */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="hidden lg:block absolute right-6 top-24 w-[360px] rotate-[-3deg]"
        >
          <div className="glass-strong rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="pulse-dot" />
                <div className="label-xs">PERFORMANCE ACCOUNT · #500104</div>
              </div>
              <div className="chip chip-green">LIVE</div>
            </div>
            <div className="mt-4">
              <div className="label-xs">SIMULATED EQUITY</div>
              <div className="text-3xl font-mono tracking-tight mt-0.5">$106,482.20</div>
              <div className="text-xs font-mono text-[#00ff66] mt-1">▲ +6.48% · +$6,482.20</div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <div>
                <div className="label-xs">REWARD</div>
                <div className="font-mono text-white">70 / 30</div>
              </div>
              <div>
                <div className="label-xs">DAILY DD</div>
                <div className="font-mono text-white">1.2%</div>
              </div>
              <div>
                <div className="label-xs">MAX DD</div>
                <div className="font-mono text-white">3.8%</div>
              </div>
            </div>
            <svg viewBox="0 0 280 70" className="w-full mt-4">
              <polyline fill="none" stroke="#00ff66" strokeWidth="1.5"
                points="0,55 20,50 40,52 60,45 80,40 100,43 120,35 140,38 160,30 180,25 200,22 220,26 240,18 260,12 280,15"/>
              <polygon fill="rgba(0,255,102,0.1)"
                points="0,55 20,50 40,52 60,45 80,40 100,43 120,35 140,38 160,30 180,25 200,22 220,26 240,18 260,12 280,15 280,70 0,70"/>
            </svg>
            <div className="mt-3 text-[11px] font-mono text-zinc-500 flex items-center justify-between">
              <span>VOL 75 · BOOM 1000 · CRASH 500</span>
              <span className="text-[#00ff66]">▲ LIVE</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* LIVE DERIV TICKER */}
      <DerivTicker />

      {/* WHY TRADERS CHOOSE US */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="features-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-sm mb-4">Why traders choose us</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Built around discipline,
              <br />
              not luck.
            </h2>
            <p className="mt-5 text-zinc-400 max-w-md">
              Our programs are designed to assess trading skill, discipline, and risk management within a structured
              environment using simulated accounts. No shortcuts, no surprises.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-tech rounded-lg p-5"
                data-testid={`feature-${i}`}
              >
                <f.icon size={20} className="text-[#00ff66]" />
                <h3 className="mt-3 font-display text-lg">{f.title}</h3>
                <p className="mt-1 text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS OVERVIEW */}
      <section className="relative border-t border-white/5 bg-[#060606] py-24" data-testid="plans-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="label-sm mb-3">Evaluation programs</div>
              <h2 className="font-display text-4xl sm:text-5xl">Choose your program</h2>
            </div>
            <Link to="/plans" className="btn-ghost btn-sm" data-testid="view-all-plans">
              View all programs <ChevronRight size={14} className="inline" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {productGrid.length === 0 && (
              <div className="col-span-full card-tech rounded-lg p-8 text-center text-sm text-zinc-500" data-testid="programs-empty">
                Program pricing is being staged — check back shortly or ping us in live chat.
              </div>
            )}
            {productGrid.map((p) => (
              <Link
                to={`/plans?type=${p.type}`}
                key={p.type}
                className="card-tech rounded-lg p-7 group hover:border-[#00ff66]/50 transition"
                data-testid={`plan-card-${p.type}`}
              >
                <div className="flex items-center justify-between">
                  <div className="label-xs">{p.tag}</div>
                  <Zap size={14} className="text-[#00ff66] opacity-70 group-hover:opacity-100" />
                </div>
                <h3 className="font-display text-3xl mt-5">{p.name}</h3>
                <p className="text-sm text-zinc-500 mt-1">{p.tagline}</p>
                <div className="grid grid-cols-2 gap-3 mt-6 text-xs">
                  <div>
                    <div className="label-xs">LEVERAGE</div>
                    <div className="font-mono text-white">{p.leverage}</div>
                  </div>
                  <div>
                    <div className="label-xs">MAX DD</div>
                    <div className="font-mono text-white">{p.max_dd}%</div>
                  </div>
                  <div>
                    <div className="label-xs">DAILY DD</div>
                    <div className="font-mono text-white">{p.daily_dd}%</div>
                  </div>
                  <div>
                    <div className="label-xs">MIN DAYS</div>
                    <div className="font-mono text-white">{p.min_days}</div>
                  </div>
                </div>
                <div className="mt-6 pt-5 border-t border-white/5 flex items-end justify-between">
                  <div>
                    <div className="label-xs">SIZES</div>
                    <div className="font-mono text-sm text-white mt-0.5">
                      {p.sizes.map(s => `$${formatInt(s.account_size)}`).join(' · ')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="label-xs">FROM</div>
                    <div className="font-mono text-xl mt-0.5">${formatInt(p.from || 0)}</div>
                  </div>
                </div>
                <div className="mt-4 text-[#00ff66] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                  Join <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="how-it-works">
        <div className="label-sm mb-3">How it works</div>
        <h2 className="font-display text-4xl sm:text-5xl max-w-3xl">A clear path from evaluation to reward.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="card-tech rounded-lg p-6 min-h-[200px] relative"
            >
              <div className="font-mono text-sm text-[#00ff66]">{s.n}</div>
              <h3 className="font-display text-xl mt-4">{s.t}</h3>
              <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CERTIFICATE SHOWCASE — renders only when admins have flagged public certs */}
      <CertificateShowcase />

      {/* PERFORMANCE REWARDS SECTION */}
      <section className="max-w-7xl mx-auto px-6 pb-20" data-testid="rewards-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-sm mb-3">Performance-based rewards</div>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.05]">
              Rewarded for <span className="text-[#00ff66]">consistency</span>, not luck.
            </h2>
            <p className="mt-5 text-zinc-400 max-w-lg">
              Traders who successfully complete the evaluation and maintain consistency are eligible to receive
              performance-based rewards, distributed in accordance with program rules and performance metrics.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-zinc-300">
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#00ff66] mt-0.5" /> Earn up to 70% reward split</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#00ff66] mt-0.5" /> Rewards based on simulated trading performance</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#00ff66] mt-0.5" /> Consistent traders unlock scaling opportunities</li>
            </ul>
          </div>
          <div className="card-tech rounded-xl p-8">
            <div className="label-sm mb-3">Reward split schedule</div>
            <div className="space-y-4">
              <RewardRow tier="First reward" pct="50%" />
              <RewardRow tier="Second reward" pct="60%" />
              <RewardRow tier="Third reward onwards" pct="70%" highlight />
            </div>
            <div className="mt-6 pt-5 border-t border-white/5 text-xs text-zinc-500">
              Rewards are distributed via cryptocurrency, typically within 24–72 hours of approval.
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="max-w-7xl mx-auto px-6 pb-20" data-testid="cta-banner">
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#060606] p-10 md:p-14">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(0,255,102,0.35), transparent)' }} />
          <div className="relative">
            <h3 className="font-display text-3xl sm:text-5xl max-w-2xl leading-tight">
              Ready to prove your edge?
            </h3>
            <p className="text-zinc-400 mt-4 max-w-xl">
              Start your evaluation from $20 with the One-Step Program, or begin directly on a simulated performance account with Instant Funding.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/plans" className="btn-primary" data-testid="cta-start">
                Start Evaluation <ArrowRight size={16} />
              </Link>
              <Link to="/faq" className="btn-ghost" data-testid="cta-faq">Have questions?</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 pb-24" data-testid="faq-section">
        <div className="label-sm mb-3">Frequently asked</div>
        <h2 className="font-display text-4xl sm:text-5xl">Answers, not hype.</h2>
        <div className="mt-8 divide-y divide-white/5 border-y border-white/5">
          {FAQS.map((f, i) => (
            <FAQItem key={i} q={f.q} a={f.a} testId={`faq-${i}`} />
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function RewardRow({ tier, pct, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-400">{tier}</span>
      <span className={`font-mono text-2xl ${highlight ? 'text-[#00ff66]' : 'text-white'}`}>{pct}</span>
    </div>
  );
}

function FAQItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5" data-testid={testId}>
      <button className="w-full flex items-center justify-between text-left" onClick={() => setOpen(v => !v)}>
        <span className="font-display text-lg">{q}</span>
        <span className={`text-[#00ff66] transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open && <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{a}</p>}
    </div>
  );
}
